/**
 * Ids, factories, and the things a supplement plan is written out as: the day
 * it was made, the tail of its name, and how often a supplement is taken.
 *
 * It sits beside the feature rather than inside `services/` for the same
 * reason `exerciseMapping` does — none of it is about the wire. What is about
 * the wire is in `supplementWire`, which is the only thing that reads a
 * snake_case field.
 */

import { DAY_LABELS, isWholeClockLabel } from "./data/supplementOptions";
import type {
  PlanSupplement,
  SupplementDose,
  SupplementDraft,
} from "./types/supplements.types";

let idCounter = 0;

/**
 * Ids for the cards and dose rows the screen creates. They never leave the
 * browser, so a counter is enough — and unlike `Math.random`, two rows added
 * in the same millisecond still differ.
 */
export const createId = (prefix: string): string =>
  `${prefix}-${(idCounter += 1).toString(36)}-${Date.now().toString(36)}`;

/**
 * The tail of a plan's name — "150926" out of
 * "Supplement Plan-2/Dia Rode/150926".
 *
 * The backend's name says three things, and a screen that is already inside
 * one client has already said two of them. So a toast or a label that names a
 * plan uses the part that tells the plans apart, and the table shows the whole
 * of it.
 *
 * The last segment rather than a parse of the shape: whatever the backend
 * decides to put in front of it, the part that identifies the plan is what it
 * ends with. A name with no separator in it is its own tail.
 */
export const shortPlanName = (name: string): string => {
  const parts = name.split("/").filter((part) => part.trim());
  return parts.length ? parts[parts.length - 1].trim() : name;
};

/**
 * The dose row a supplement opens with: nothing picked on it.
 *
 * It used to open before breakfast at eight, and a card saved untouched went
 * up saying so — a time nobody chose, read by a client as one their
 * dietitian did. Every list on the row now shows its placeholder until it is
 * picked from, and `isSupplementComplete` holds the card until all of them
 * have been.
 */
export const createDose = (): SupplementDose => ({
  id: createId("dose"),
  when: "",
  meal: "",
  mealSlotTypeId: null,
  time: "",
});

/**
 * A blank supplement card.
 *
 * It opens on one dose row. It used to open on two, pre-filled, which was
 * what said the list grows — but a row that opens blank is a row that has to
 * be filled in or taken away before the card can be saved, and a second one
 * nobody asked for is a chore on every once-a-day supplement. The plus under
 * the rows is what says the list grows now.
 *
 * The unit is left empty and filled in by the card once
 * `/api/v1/supplement-units/` has answered — a factory that guessed at one
 * would put a unit on the card that the catalog may not even hold.
 */
export const createSupplementDraft = (): SupplementDraft => ({
  id: createId("supplement"),
  name: "",
  dosage: "",
  unit: "",
  unitId: null,
  frequency: "everyday",
  // Every day ticked, which is what "Everyday" means — so switching the
  // frequency to named days starts from all of them rather than from none.
  days: [0, 1, 2, 3, 4, 5, 6],
  doses: [createDose()],
  note: "",
  saved: false,
});

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

/** "02 Jan, 2026" — how the table writes a date. */
export const formatSupplementDate = (value: string | null): string => {
  const date = new Date(value ?? "");
  if (Number.isNaN(date.getTime())) return "-";

  const day = String(date.getDate()).padStart(2, "0");
  return `${day} ${MONTHS[date.getMonth()]}, ${date.getFullYear()}`;
};

/**
 * What the Dose box will take: a figure, and nothing else.
 *
 * The unit is picked beside it, so letters in the box are the unit written
 * twice — "500mg" against mg. Digits and one point are kept, everything
 * else is dropped as it is typed, so a paste of "1000 mg" lands as "1000".
 * Still a string: the wire's `dosage_strength` is free text, and "0.5"
 * should go up as typed rather than as whatever a number makes of it.
 */
export const numericDose = (value: string): string => {
  const [whole, ...fraction] = value.replace(/[^\d.]/g, "").split(".");
  return fraction.length ? `${whole}.${fraction.join("")}` : whole;
};

/**
 * One dose, as a card and the send dialog read it: "5 mg", "2 scoop",
 * "1000 mg tablet".
 *
 * The strength as typed, and the unit after it — one line, since together
 * they are one fact. The unit is not repeated where the strength already
 * ends in it: "500mg" against mg is "500mg", not "500mg mg". Nothing is
 * parsed — the strength is free text on the wire and stays so here.
 */
export const doseLabel = (
  supplement: Pick<PlanSupplement, "dosage" | "unit">
): string => {
  const dosage = supplement.dosage.trim();
  const unit = supplement.unit.trim().toLowerCase();

  if (!dosage) return unit;
  if (!unit || dosage.toLowerCase().endsWith(unit)) return dosage;
  return `${dosage} ${unit}`;
};

/**
 * How often it is taken, as a card reads it: "Daily", or the days themselves —
 * "Mon, Tue, Thu".
 *
 * Every day named is "Daily" too, whichever way the frequency was set. The two
 * say the same thing, and a line reading "Mon, Tue, Wed, Thu, Fri, Sat, Sun"
 * would be the long way of saying the short one.
 */
export const frequencyLabel = (
  supplement: Pick<PlanSupplement, "frequency" | "days">
): string => {
  const days = [...supplement.days].sort((a, b) => a - b);

  if (supplement.frequency === "everyday" || days.length === DAY_LABELS.length)
    return "Daily";

  // Nothing ticked is not a schedule. Said as what it is rather than as an
  // empty cell, which would read as unknown.
  if (days.length === 0) return "No days chosen";

  return days.map((day) => DAY_LABELS[day]).join(", ");
};

/**
 * A fetched supplement as a card holds one.
 *
 * It comes in `saved`, which is what puts it in front of the reader as
 * something to read rather than as a screen of dropdowns — see
 * `SupplementCard`.
 */
export const toSupplementDraft = (
  supplement: PlanSupplement
): SupplementDraft => ({ ...supplement, saved: true });

/**
 * Whether a card has been started at all.
 *
 * The name alone — that is what tells a card somebody is filling in from one
 * they opened and left. It is not enough to save: see `isSupplementComplete`.
 */
export const isSupplementWritten = (draft: SupplementDraft): boolean =>
  draft.name.trim().length > 0;

/**
 * And whether it says enough to be worth storing.
 *
 * What it is, how much of it, what that is counted in, a day to take it on,
 * and every dose whole: before or after, which meal, and at what time.
 *
 * The unit and the meal are about the write as much as the reader: both are
 * sent as catalog ids, so a card holding a unit or a meal that no catalog row
 * is behind is one the backend would refuse. The unit fills itself in as soon
 * as its catalog answers — see `CreateSupplementPage`; a dose is the
 * dietitian's to write, and a row still showing its placeholders is what
 * holds the card back.
 *
 * This is what the card's own Save waits on, the way a meal option's Save
 * waits until one dish row is whole: there is nothing to store until it is,
 * and nothing to read back if it were stored.
 */
/**
 * Whether every list on a dose row has been picked from — the time counted
 * as three of them, since it is picked as an hour, a minute and a half of
 * the day and is written with holes until all three are.
 */
export const isDoseComplete = (dose: SupplementDose): boolean =>
  dose.when !== "" &&
  dose.mealSlotTypeId != null &&
  isWholeClockLabel(dose.time);

export const isSupplementComplete = (draft: SupplementDraft): boolean =>
  isSupplementWritten(draft) &&
  draft.dosage.trim().length > 0 &&
  draft.unitId != null &&
  draft.days.length > 0 &&
  draft.doses.length > 0 &&
  draft.doses.every(isDoseComplete);

/**
 * Whether two supplements say the same thing — the same name, dose, unit,
 * schedule, doses and note — whatever their ids and whether either is saved.
 *
 * What the leave-flush on the create screen asks before writing: a card
 * reopened with Modify and left as it was is not a change, and writing a plan
 * that already holds exactly this would be a request for nothing. Only the
 * fields the wire carries are compared, so a dose row's browser id or a stray
 * `saved` flag does not count as a difference.
 */
export const isSameSupplement = (
  a: PlanSupplement,
  b: PlanSupplement
): boolean => {
  const contentOf = (supplement: PlanSupplement) =>
    JSON.stringify({
      name: supplement.name.trim(),
      dosage: supplement.dosage.trim(),
      unitId: supplement.unitId,
      frequency: supplement.frequency,
      days: [...supplement.days].sort((x, y) => x - y),
      doses: supplement.doses.map((dose) => [
        dose.when,
        dose.mealSlotTypeId,
        dose.time,
      ]),
      note: supplement.note.trim(),
    });

  return contentOf(a) === contentOf(b);
};
